import { OnDestroy, TemplateRef, Injectable } from "@angular/core";
import { MainState } from "@app/store/state";
import { BortleScale } from "@core/interfaces/deep-sky-acquisition.interface";
import { SubjectType } from "@core/interfaces/image.interface";
import { SeeingScale, TransparencyScale } from "@core/interfaces/solar-system-acquisition.interface";
import { ImageService } from "@core/services/image/image.service";
import { LoadingService } from "@core/services/loading.service";
import { distinctUntilChangedObj, UtilsService } from "@core/services/utils/utils.service";
import { LoadEquipmentItem } from "@features/equipment/store/equipment.actions";
import { selectEquipmentItem, selectEquipmentItems } from "@features/equipment/store/equipment.selectors";
import { EquipmentItemType } from "@features/equipment/types/equipment-item-base.interface";
import { AdditionalDeepSkyAcquisitionPropertiesModalComponent } from "@features/image/components/additional-deep-sky-acquisition-properties-modal/additional-deep-sky-acquisition-properties-modal.component";
import { AdditionalSolarSystemAcquisitionPropertiesModalComponent } from "@features/image/components/additional-solar-system-acquisition-properties-modal/additional-solar-system-acquisition-properties-modal.component";
import { AcquisitionForm } from "@features/image/components/override-acquisition-form-modal/override-acquisition-form-modal.component";
import { ImageEditFieldsBaseService } from "@features/image/services/image-edit-fields-base.service";
import { ImageEditService } from "@features/image/services/image-edit.service";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";

@Injectable({
  providedIn: null
})
export class ImageEditAcquisitionFieldsService extends ImageEditFieldsBaseService implements OnDestroy {
  acquisitionFilterSelectFooterTemplateExtra: TemplateRef<any>;

  acquisitionAdditionalButtonsTemplate: TemplateRef<any>;

  private _subjectTypeChangesSubscription: Subscription;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly loadingService: LoadingService,
    public readonly translateService: TranslateService,
    public readonly imageService: ImageService,
    public readonly imageEditService: ImageEditService,
    public readonly modalService: NgbModal,
    public readonly utilsService: UtilsService
  ) {
    super(loadingService);
  }

  onFieldsInitialized(): void {
    const field = this.imageEditService.form.get("subjectType");

    if (field) {
      this._subjectTypeChangesSubscription = field.valueChanges.subscribe(subjectType => {
        const stepper = this.imageEditService.fields.find(field => field.id === "image-stepper-field");
        if (!stepper || !stepper.fieldGroup) {
          return;
        }

        const acquisitionStep = stepper.fieldGroup.find(field => field.id === "image-stepper-acquisition");
        if (!acquisitionStep || !acquisitionStep.fieldGroup) {
          return;
        }

        const field = acquisitionStep.fieldGroup.find(field => field.key === "unsupportedSubjectTypeForAcquisitions");
        if (field) {
          field.template = this._getUnsupportedSubjectTypeForAcquisitionsTemplate(subjectType);
        }
      });
    }
  }

  getFields(): FormlyFieldConfig[] {
    return this.getDeepSkyFields().concat(this.getSolarSystemFields(), this.getUnsupportedSubjectTypeFields());
  }

  getDeepSkyFields(): FormlyFieldConfig[] {
    const fields: FormlyFieldConfig[] = [];

    // Warning field group with message and button
    fields.push({
      key: "_orphanedFiltersWarning",
      wrappers: [],
      className: "alert alert-warning orphaned-filters-alert mb-3",
      hideExpression: (model, formState, field) => {
        if (!this.imageEditService.model?.deepSkyAcquisitions?.length) {
          return true;
        }

        const hasOrphaned = this.imageEditService.model.deepSkyAcquisitions.some(
          acq =>
            acq.filter2 &&
            (!this.imageEditService.model.filters2 || this.imageEditService.model.filters2.indexOf(acq.filter2) === -1)
        );

        return !hasOrphaned;
      },
      fieldGroup: [
        {
          key: "_orphanedFiltersMessage",
          type: "formly-template",
          wrappers: [],
          expressionProperties: {
            template: model => {
              if (!this.imageEditService.model?.deepSkyAcquisitions?.length) {
                return "";
              }

              const orphanedAcquisitions = this.imageEditService.model.deepSkyAcquisitions.filter(
                acq =>
                  acq.filter2 &&
                  (!this.imageEditService.model.filters2 ||
                    this.imageEditService.model.filters2.indexOf(acq.filter2) === -1)
              );

              if (orphanedAcquisitions.length === 0) {
                return "";
              }

              // Load filter information for all orphaned acquisitions
              const filterPromises = orphanedAcquisitions.map(acq => {
                this.store$.dispatch(new LoadEquipmentItem({ id: acq.filter2, type: EquipmentItemType.FILTER }));
                return this.store$
                  .select(selectEquipmentItem, { id: acq.filter2, type: EquipmentItemType.FILTER })
                  .pipe(take(1))
                  .toPromise()
                  .then(filter => ({ acq, filter }));
              });

              // Build the HTML synchronously for now, filters will load asynchronously
              let listHtml = "";
              orphanedAcquisitions.forEach(acq => {
                // Get filter name from store if available
                let filterName = "";
                this.store$
                  .select(selectEquipmentItem, { id: acq.filter2, type: EquipmentItemType.FILTER })
                  .pipe(take(1))
                  .subscribe(filter => {
                    if (filter) {
                      filterName = `${filter.brandName} ${filter.name}`;
                    } else {
                      // If not loaded yet, dispatch load action and use ID temporarily
                      this.store$.dispatch(new LoadEquipmentItem({ id: acq.filter2, type: EquipmentItemType.FILTER }));
                      filterName = `Loading filter ${acq.filter2}...`;
                    }
                  });

                const date = acq.date ? ` (${acq.date})` : "";
                const details = `${acq.number}×${acq.duration}s${date}`;
                listHtml += `<li><strong>${filterName}</strong>: ${details}</li>`;
              });

              return `
                <div class="small">
                  <h6 class="alert-heading">
                    <strong>${this.translateService.instant("Acquisition data issues found")}</strong>
                  </h6>
                  <p class="mb-2">
                    ${
                      orphanedAcquisitions.length === 1
                        ? this.translateService.instant(
                            "1 acquisition uses a filter not in this image's equipment list:"
                          )
                        : this.translateService.instant(
                            "{{0}} acquisitions use filters not in this image's equipment list:",
                            { 0: orphanedAcquisitions.length }
                          )
                    }
                  </p>
                  <ul class="mb-3">
                    ${listHtml}
                  </ul>
                  <div class="mb-0 fst-italic">
                    <p class="mb-1">${
                      orphanedAcquisitions.length === 1
                        ? this.translateService.instant(
                            "This may have been copied from another image by mistake. To resolve:"
                          )
                        : this.translateService.instant(
                            "These may have been copied from another image by mistake. To resolve:"
                          )
                    }</p>
                    <ol class="mb-0">
                      <li>${
                        orphanedAcquisitions.length === 1
                          ? this.translateService.instant("Delete the incorrect session using the trash icon")
                          : this.translateService.instant("Delete incorrect sessions using the trash icons")
                      }</li>
                      <li>${
                        orphanedAcquisitions.length === 1
                          ? this.translateService.instant("Keep the session and add its filter in the Equipment step")
                          : this.translateService.instant(
                              "Keep the sessions and add their filters in the Equipment step"
                            )
                      }</li>
                      <li>${
                        orphanedAcquisitions.length === 1
                          ? this.translateService.instant("Replace with a filter from this image's equipment")
                          : this.translateService.instant("Replace with filters from this image's equipment")
                      }</li>
                    </ol>
                  </div>
                </div>
              `;
            }
          }
        },
        {
          key: "_removeOrphanedButton",
          type: "button",
          wrappers: [],
          className: "mt-3",
          props: {
            label: this.translateService.instant("Remove all affected"),
            className: "btn btn-danger btn-sm",
            click: (field, event) => {
              this._removeOrphanedAcquisitions();
            }
          }
        }
      ]
    });

    fields.push({
      key: "deepSkyAcquisitions",
      type: "table",
      hideExpression: () => {
        if (!this.imageEditService.model.overrideAcquisitionForm) {
          return !this.imageEditService.isLongExposure();
        }

        return this.imageEditService.model.overrideAcquisitionForm !== AcquisitionForm.LONG_EXPOSURE;
      },
      props: {
        required: false,
        label: this.translateService.instant("Long exposure acquisition sessions"),
        addLabel: this.translateService.instant("Add session"),
        additionalPropertiesClicked: (index: number) => {
          const modalRef: NgbModalRef = this.modalService.open(AdditionalDeepSkyAcquisitionPropertiesModalComponent, {
            backdrop: "static"
          });
          const componentInstance: AdditionalDeepSkyAcquisitionPropertiesModalComponent = modalRef.componentInstance;
          componentInstance.imageEditService = this.imageEditService;
          componentInstance.fieldGroup = this.getAdditionalDeepSkyFields();
          componentInstance.index = index;
        },
        additionalButtonsTemplate: this.acquisitionAdditionalButtonsTemplate,
        excludeFromCountNonNullProperties: [
          "date",
          "filter2",
          "number",
          "duration",
          "id",
          "image",
          "filter",
          "savedOn",
          "advanced",
          "moonIllumination",
          "filter2Brand",
          "filter2Name",
          "filter2Type",
          "filterMake",
          "filterName",
          "filterType"
        ]
      },
      fieldArray: {
        fieldGroup: [
          this._getDateField(),
          this._getFilterField(),
          this._getNumberField(),
          this._getDurationField(),
          {
            fieldGroup: this.getAdditionalDeepSkyFields(),
            props: {
              hide: true
            }
          }
        ]
      }
    });

    return fields;
  }

  getAdditionalDeepSkyFields(): FormlyFieldConfig[] {
    return [
      {
        key: "isSynthetic",
        type: "checkbox",
        wrappers: ["default-wrapper"],
        props: {
          label: this.translateService.instant("Synthetic channel"),
          required: false
        }
      },
      {
        wrappers: ["card-wrapper"],
        hideExpression: "model.isSynthetic",
        templateOptions: {
          label: this.translateService.instant("Sensor")
        },
        fieldGroup: [
          {
            key: "iso",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: "ISO",
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: ["whole-number"]
            }
          },
          {
            key: "binning",
            type: "ng-select",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Binning"),
              required: false,
              options: [1, 2, 3, 4].map(x => ({ value: x, label: `${x}x${x}` })),
              searchable: false,
              clearable: true
            }
          },
          {
            key: "gain",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: "Gain",
              required: false,
              step: 1,
              min: -99999.99,
              max: 99999.99
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          },
          {
            key: "sensorCooling",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Cooling"),
              description: this.translateService.instant("The temperature of the chip in Celsius degrees, e.g. -20."),
              required: false,
              step: 1,
              min: -274,
              max: 100
            },
            validators: {
              validation: ["whole-number"]
            }
          }
        ]
      },
      {
        wrappers: ["card-wrapper"],
        hideExpression: "model.isSynthetic",
        templateOptions: {
          label: this.translateService.instant("Optics")
        },
        fieldGroup: [
          {
            key: "fNumber",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("f-number"),
              description: this.translateService.instant(
                "If you used a camera lens, please specify the f-number (also known as f-ratio or f-stop) that you " +
                  "used for this acquisition session."
              ),
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          }
        ]
      },
      {
        wrappers: ["card-wrapper"],
        hideExpression: "model.isSynthetic",
        templateOptions: {
          label: this.translateService.instant("Calibration")
        },
        fieldGroup: [
          {
            key: "darks",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Darks"),
              description: this.translateService.instant("The number of dark frames."),
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: ["whole-number"]
            }
          },
          {
            key: "flats",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Flats"),
              description: this.translateService.instant("The number of flat frames."),
              required: false,
              step: 1,
              min: 0
            }
          },
          {
            key: "flatDarks",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Flat darks"),
              description: this.translateService.instant("The number of flat dark frames."),
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: ["whole-number"]
            }
          },
          {
            key: "bias",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Bias"),
              description: this.translateService.instant("The number of bias/offset frames."),
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: ["whole-number"]
            }
          }
        ]
      },
      {
        wrappers: ["card-wrapper"],
        hideExpression: "model.isSynthetic",
        templateOptions: {
          label: "Sky/Ambient conditions"
        },
        fieldGroup: [
          {
            key: "bortle",
            type: "ng-select",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Bortle dark-sky scale"),
              description: this.translateService.instant(
                'Quality of the sky according to <a href="http://en.wikipedia.org/wiki/Bortle_Dark-Sky_Scale" target="_blank">the Bortle Scale</a>.'
              ),
              required: false,
              options: [
                {
                  value: BortleScale.ONE,
                  label: this.imageService.humanizeBortleScale(BortleScale.ONE)
                },
                {
                  value: BortleScale.TWO,
                  label: this.imageService.humanizeBortleScale(BortleScale.TWO)
                },
                {
                  value: BortleScale.THREE,
                  label: this.imageService.humanizeBortleScale(BortleScale.THREE)
                },
                {
                  value: BortleScale.FOUR,
                  label: this.imageService.humanizeBortleScale(BortleScale.FOUR)
                },
                {
                  value: BortleScale.FOUR_POINT_FIVE,
                  label: this.imageService.humanizeBortleScale(BortleScale.FOUR_POINT_FIVE)
                },
                {
                  value: BortleScale.FIVE,
                  label: this.imageService.humanizeBortleScale(BortleScale.FIVE)
                },
                {
                  value: BortleScale.SIX,
                  label: this.imageService.humanizeBortleScale(BortleScale.SIX)
                },
                {
                  value: BortleScale.SEVEN,
                  label: this.imageService.humanizeBortleScale(BortleScale.SEVEN)
                },
                {
                  value: BortleScale.EIGHT,
                  label: this.imageService.humanizeBortleScale(BortleScale.EIGHT)
                },
                {
                  value: BortleScale.NINE,
                  label: this.imageService.humanizeBortleScale(BortleScale.NINE)
                }
              ],
              searchable: false,
              clearable: true
            }
          },
          {
            key: "meanSqm",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Mean mag/arcsec^2"),
              description: this.translateService.instant(
                "Mean SQM mag/arcsec^2 as measured by your Sky Quality Meter."
              ),
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          },
          {
            key: "meanFwhm",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Mean FWHM"),
              description: this.translateService.instant(
                "Mean Full Width at Half Maximum in arc seconds, a measure of seeing."
              ),
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          },
          {
            key: "temperature",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Temperature"),
              description: this.translateService.instant("Ambient temperature in Celsius degrees."),
              required: false,
              step: 1,
              min: -88, // Minimum temperature ever recorded on Earth.
              max: 58 // Maximum temperature ever recorded on Earth.
            },
            validators: {
              validation: [
                "number",
                {
                  name: "min-value",
                  options: {
                    minValue: -88
                  }
                },
                {
                  name: "max-value",
                  options: {
                    maxValue: 58
                  }
                },
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          }
        ]
      }
    ];
  }

  getSolarSystemFields(): FormlyFieldConfig[] {
    return [
      {
        key: "solarSystemAcquisitions",
        type: "table",
        hideExpression: () => {
          if (!this.imageEditService.model.overrideAcquisitionForm) {
            return !this.imageEditService.isVideoBased();
          }

          return this.imageEditService.model.overrideAcquisitionForm !== AcquisitionForm.VIDEO_BASED;
        },
        props: {
          required: false,
          label: this.translateService.instant("Video based acquisition sessions"),
          addLabel: this.translateService.instant("Add session"),
          maxRows: 1,
          additionalPropertiesClicked: (index: number) => {
            const modalRef: NgbModalRef = this.modalService.open(
              AdditionalSolarSystemAcquisitionPropertiesModalComponent,
              {
                backdrop: "static"
              }
            );
            const componentInstance: AdditionalSolarSystemAcquisitionPropertiesModalComponent =
              modalRef.componentInstance;
            componentInstance.imageEditService = this.imageEditService;
            componentInstance.fieldGroup = this.getAdditionalSolarSystemFields();
            componentInstance.index = index;
          },
          additionalButtonsTemplate: this.acquisitionAdditionalButtonsTemplate,
          excludeFromCountNonNullProperties: [
            "id",
            "date",
            "time",
            "image",
            "frames",
            "fps",
            "exposurePerFrame",
            "savedOn",
            "moonIllumination"
          ]
        },
        fieldArray: {
          fieldGroup: [
            this._getDateField(),
            this._getTimeField(),
            this._getFramesField(),
            this._getFPSField(),
            this._getExposurePerFrame(),
            {
              fieldGroup: this.getAdditionalSolarSystemFields(),
              props: {
                hide: true
              }
            }
          ]
        }
      }
    ];
  }

  getAdditionalSolarSystemFields(): FormlyFieldConfig[] {
    return [
      {
        wrappers: ["card-wrapper"],
        templateOptions: {
          label: this.translateService.instant("Optics")
        },
        fieldGroup: [
          {
            key: "focalLength",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Focal length") + " mm",
              description: this.translateService.instant(
                "The focal length of the whole optical train, including barlow lenses or other components."
              ),
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: ["whole-number"]
            }
          }
        ]
      },
      {
        wrappers: ["card-wrapper"],
        templateOptions: {
          label: this.translateService.instant("Camera")
        },
        fieldGroup: [
          {
            key: "iso",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: "ISO",
              required: false,
              step: 1,
              min: 0
            },
            validators: {
              validation: ["whole-number"]
            }
          },
          {
            key: "gain",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: "Gain",
              required: false,
              step: 1,
              min: -99999.99,
              max: 99999.99
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          }
        ]
      },
      {
        wrappers: ["card-wrapper"],
        templateOptions: {
          label: "Sky/Ambient conditions"
        },
        fieldGroup: [
          {
            key: "seeing",
            type: "ng-select",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Seeing"),
              description: this.translateService.instant("Your estimation of the seeing."),
              required: false,
              options: [
                { value: SeeingScale.VERY_BAD, label: this.translateService.instant("Very bad") },
                { value: SeeingScale.BAD, label: this.translateService.instant("Bad") },
                { value: SeeingScale.ACCEPTABLE, label: this.translateService.instant("Acceptable") },
                { value: SeeingScale.GOOD, label: this.translateService.instant("Good") },
                { value: SeeingScale.EXCELLENT, label: this.translateService.instant("Excellent") }
              ],
              searchable: false,
              clearable: true
            }
          },
          {
            key: "transparency",
            type: "ng-select",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("Transparency"),
              description: this.translateService.instant("Your estimation of the transparency."),
              required: false,
              options: [
                { value: TransparencyScale.EXTREMELY_BAD, label: this.translateService.instant("Extremely bad") },
                { value: TransparencyScale.VERY_BAD, label: this.translateService.instant("Very bad") },
                { value: TransparencyScale.BAD, label: this.translateService.instant("Bad") },
                { value: TransparencyScale.MEDIOCRE, label: this.translateService.instant("Mediocre") },
                { value: TransparencyScale.ACCEPTABLE, label: this.translateService.instant("Acceptable") },
                { value: TransparencyScale.FAIR, label: this.translateService.instant("Fair") },
                { value: TransparencyScale.GOOD, label: this.translateService.instant("Good") },
                { value: TransparencyScale.VERY_GOOD, label: this.translateService.instant("Very good") },
                { value: TransparencyScale.EXCELLENT, label: this.translateService.instant("Excellent") },
                { value: TransparencyScale.SUPERB, label: this.translateService.instant("Superb") }
              ],
              searchable: false,
              clearable: true
            }
          }
        ]
      },
      {
        wrappers: ["card-wrapper"],
        templateOptions: {
          label: "Central meridians"
        },
        fieldGroup: [
          {
            key: "cmi",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("CMI"),
              description: this.translateService.instant("Latitude of the first Central Meridian."),
              required: false,
              step: 1,
              min: 0,
              max: 359.99
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          },
          {
            key: "cmii",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("CMII"),
              description: this.translateService.instant("Latitude of the second Central Meridian."),
              required: false,
              step: 1,
              min: 0,
              max: 359.99
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          },
          {
            key: "cmiii",
            type: "custom-number",
            wrappers: ["default-wrapper"],
            props: {
              label: this.translateService.instant("CMIII"),
              description: this.translateService.instant("Latitude of the third Central Meridian."),
              required: false,
              step: 1,
              min: 0,
              max: 359.99
            },
            validators: {
              validation: [
                "number",
                {
                  name: "max-decimals",
                  options: {
                    value: 2
                  }
                }
              ]
            }
          }
        ]
      }
    ];
  }

  getUnsupportedSubjectTypeFields(): FormlyFieldConfig[] {
    const subjectType: string = this.imageService.humanizeSubjectType(this.imageEditService.model.subjectType);
    const message: string = this.translateService.instant(
      'The subject type "{{0}}" does not support acquisition sessions.',
      {
        0: subjectType
      }
    );

    return [
      {
        className: "section-label",
        template: `<p>${message}</p>`,
        hideExpression: () => this.imageEditService.isDeepSky() || this.imageEditService.isSolarSystem(),
        key: "unsupportedSubjectTypeForAcquisitions",
        props: {
          required: false
        }
      }
    ];
  }

  private _getUnsupportedSubjectTypeForAcquisitionsTemplate(subjectType: SubjectType): string {
    const value: string = this.imageService.humanizeSubjectType(subjectType);
    const message: string = this.translateService.instant(
      'The subject type "{{0}}" does not support acquisition sessions.',
      {
        0: value
      }
    );

    return `<p>${message}</p>`;
  }

  private _getDateField(): FormlyFieldConfig {
    const supportsDateInput = this.utilsService.supportsDateInput();

    const now = new Date();
    now.setHours(23, 59, 59, 999);

    return {
      key: "date",
      type: "input",
      wrappers: ["default-wrapper"],
      props: {
        type: supportsDateInput ? "date" : "text",
        label: this.translateService.instant("Date"),
        description: this.translateService.instant("Acquisition date."),
        required: false,
        placeholder: supportsDateInput ? "" : "YYYY-MM-DD"
      },
      validators: {
        validation: [
          {
            name: "is-date",
            options: {
              format: supportsDateInput ? UtilsService.getDateFormatString() : "YYYY-MM-DD"
            }
          },
          {
            name: "min-date",
            options: {
              value: new Date(1000, 0, 1)
            }
          },
          {
            name: "max-date",
            options: {
              value: now
            }
          }
        ]
      }
    };
  }

  private _getFilterField(): FormlyFieldConfig {
    // Load orphaned filters once when creating the field
    const orphanedFilterIds: number[] = [];
    if (this.imageEditService.model?.deepSkyAcquisitions) {
      this.imageEditService.model.deepSkyAcquisitions.forEach(acq => {
        if (
          acq.filter2 &&
          (!this.imageEditService.model.filters2 || this.imageEditService.model.filters2.indexOf(acq.filter2) === -1)
        ) {
          if (!orphanedFilterIds.includes(acq.filter2)) {
            orphanedFilterIds.push(acq.filter2);
            // Dispatch action to load the orphaned filter
            this.store$.dispatch(new LoadEquipmentItem({ id: acq.filter2, type: EquipmentItemType.FILTER }));
          }
        }
      });
    }

    const field: FormlyFieldConfig = {
      key: "filter2",
      type: "ng-select",
      wrappers: ["default-wrapper"],
      props: {
        label: this.translateService.instant("Filter"),
        description:
          this.translateService.instant("Filter used.") +
          " " +
          this.translateService.instant("To add more, go back to the Equipment step."),
        clearable: true,
        searchable: true,
        footerTemplateExtra: this.acquisitionFilterSelectFooterTemplateExtra,
        options: this.store$.select(selectEquipmentItems).pipe(
          takeUntil(this.destroyed$),
          map(items => {
            // Include both regular filters and orphaned filters
            return items.filter(
              item =>
                item.klass === EquipmentItemType.FILTER &&
                (this.imageEditService.model.filters2.indexOf(item.id) > -1 || orphanedFilterIds.includes(item.id))
            );
          }),
          map(items =>
            items.map(item => ({
              value: item.id,
              label: `${item.brandName} ${item.name}`,
              // Mark orphaned filters as disabled
              disabled: this.imageEditService.model.filters2?.indexOf(item.id) === -1
            }))
          ),
          map(items => UtilsService.sortObjectsByProperty(items, "label")),
          distinctUntilChangedObj()
        ),
        appendTo: "body"
      },
      expressionProperties: {
        className: model => {
          // Add warning class if filter is not in equipment list
          if (model.filter2 && this.imageEditService.model.filters2?.indexOf(model.filter2) === -1) {
            return "orphaned-filter-warning";
          }
          return "";
        }
      }
    };

    return field;
  }

  private _getNumberField(): FormlyFieldConfig {
    return {
      key: "number",
      type: "input",
      wrappers: ["default-wrapper"],
      props: {
        label: this.translateService.instant("Number"),
        description: this.translateService.instant("Number of frames."),
        required: true,
        step: 1,
        min: 1
      },
      validators: {
        validation: ["number", "whole-number"]
      }
    };
  }

  private _getDurationField(): FormlyFieldConfig {
    return {
      key: "duration",
      type: "custom-number",
      wrappers: ["default-wrapper"],
      props: {
        label: this.translateService.instant("Duration"),
        description: this.translateService.instant("Duration of each frame in seconds."),
        required: true,
        step: 1,
        min: 0.0001,
        max: 999999.9999
      },
      validators: {
        validation: [
          "number",
          {
            name: "max-decimals",
            options: {
              value: 4
            }
          }
        ]
      }
    };
  }

  private _getTimeField(): FormlyFieldConfig {
    return {
      key: "time",
      type: "input",
      wrappers: ["default-wrapper"],
      props: {
        type: "time",
        label: this.translateService.instant("Time"),
        description: this.translateService.instant("Acquisition time (local timezone)."),
        required: false
      }
    };
  }

  private _getFramesField(): FormlyFieldConfig {
    return {
      key: "frames",
      type: "custom-number",
      wrappers: ["default-wrapper"],
      props: {
        label: this.translateService.instant("Frames"),
        description: this.translateService.instant("Number of frames."),
        required: false,
        step: 1,
        min: 1
      },
      validators: {
        validation: ["whole-number"]
      }
    };
  }

  private _getFPSField(): FormlyFieldConfig {
    return {
      key: "fps",
      type: "custom-number",
      wrappers: ["default-wrapper"],
      props: {
        label: this.translateService.instant("FPS"),
        description: this.translateService.instant("Frames per second."),
        required: false,
        step: 1,
        min: 0.00001
      },
      validators: {
        validation: [
          "number",
          {
            name: "max-decimals",
            options: {
              value: 5
            }
          }
        ]
      }
    };
  }

  private _getExposurePerFrame(): FormlyFieldConfig {
    return {
      key: "exposurePerFrame",
      type: "custom-number",
      wrappers: ["default-wrapper"],
      props: {
        label: this.translateService.instant("Exposure per frame") + " ms",
        required: false,
        step: 1,
        min: 0.01,
        max: 99999.99
      },
      validators: {
        validation: [
          "number",
          {
            name: "max-decimals",
            options: {
              value: 2
            }
          }
        ]
      }
    };
  }

  private _removeOrphanedAcquisitions(): void {
    if (!this.imageEditService.model?.deepSkyAcquisitions) {
      return;
    }

    // Find the deepSkyAcquisitions field in the form structure
    const stepper = this.imageEditService.fields.find(field => field.id === "image-stepper-field");
    if (!stepper || !stepper.fieldGroup) {
      return;
    }

    const acquisitionStep = stepper.fieldGroup.find(field => field.id === "image-stepper-acquisition");
    if (!acquisitionStep || !acquisitionStep.fieldGroup) {
      return;
    }

    const deepSkyField = acquisitionStep.fieldGroup.find(field => field.key === "deepSkyAcquisitions");
    if (!deepSkyField) {
      return;
    }

    // Get indices of orphaned acquisitions to remove (in reverse order to avoid index shifting)
    const indicesToRemove: number[] = [];
    this.imageEditService.model.deepSkyAcquisitions.forEach((acq, index) => {
      if (
        acq.filter2 &&
        (!this.imageEditService.model.filters2 || this.imageEditService.model.filters2.indexOf(acq.filter2) === -1)
      ) {
        indicesToRemove.push(index);
      }
    });

    // Remove items from the end to the beginning to avoid index shifting issues
    indicesToRemove.sort((a, b) => b - a);

    // Get the FormArray control
    const formArray = this.imageEditService.form.get("deepSkyAcquisitions") as any;

    // Remove each orphaned acquisition properly using the FormArray's removeAt method
    indicesToRemove.forEach(index => {
      // Remove from model
      this.imageEditService.model.deepSkyAcquisitions.splice(index, 1);

      // Remove from FormArray
      if (formArray && formArray.removeAt) {
        formArray.removeAt(index);
      }

      // Remove from field group
      if (deepSkyField.fieldGroup) {
        deepSkyField.fieldGroup.splice(index, 1);
        // Re-index remaining fields
        deepSkyField.fieldGroup.forEach((f, key) => {
          if (f.key !== undefined) {
            f.key = `${key}`;
          }
        });
      }
    });

    // Trigger form update and mark as dirty
    this.imageEditService.form.updateValueAndValidity();
    if (formArray && formArray.markAsDirty) {
      formArray.markAsDirty();
    }
  }
}
